import type { Schema } from "amplify/data/resource";
import { phoneValidator } from "@cpos/shared";
import {
  addressColumnsOf,
  addressDraftFrom,
  addressError,
  formatAddress,
  normalizeAddress,
  type AddressDraft,
  type FieldChange,
} from "./business_info_edit";

type BusinessProfile = Schema["BusinessProfile"]["type"];

/** The nested record that holds the person the Profile sheet shows. */
export type PersonRecord = "individual" | "representative";

export interface PersonalInfoDraft {
  phone: string;
  address: AddressDraft;
}

export type PersonalInfoField = "personalPhone" | "personalAddress";

export type PersonalInfoErrors = Partial<Record<PersonalInfoField, string>>;

/** Fields a personal save may set. There is no `company`, so a patch never carries business data. */
export type PersonalInfoPatch = Pick<
  Schema["BusinessProfile"]["updateType"],
  "id" | "individual" | "representative"
>;

type Person =
  | { record: "individual"; person: NonNullable<BusinessProfile["individual"]> }
  | {
      record: "representative";
      person: NonNullable<BusinessProfile["representative"]>;
    };

/**
 * The record behind the Profile sheet's contact section: `individual` for an
 * individual business, `representative` otherwise. Null when it has none.
 */
function personOf(business: BusinessProfile): Person | null {
  if (business.businessType === "individual") {
    return business.individual
      ? { record: "individual", person: business.individual }
      : null;
  }
  return business.representative
    ? { record: "representative", person: business.representative }
    : null;
}

/** Which nested record holds the person the Profile sheet shows, or null when there is none. */
export function personRecordOf(business: BusinessProfile): PersonRecord | null {
  return personOf(business)?.record ?? null;
}

/** The person's first and last name, each trimmed; empty without a person record. */
export function personNameOf(business: BusinessProfile): string {
  const person = personOf(business)?.person;
  return [person?.firstName, person?.lastName]
    .map((name) => name?.trim())
    .filter(Boolean)
    .join(" ");
}

/** The editable values as stored. Reads the person record only, never `company`. */
export function personalDraftFrom(
  business: BusinessProfile,
): PersonalInfoDraft {
  const person = personOf(business)?.person;
  return {
    phone: person?.phone ?? "",
    address: addressDraftFrom(person, business.businessCountry),
  };
}

/** Fields whose saved value would differ from the stored one. */
export function diffPersonalInfo(
  before: PersonalInfoDraft,
  edited: PersonalInfoDraft,
): FieldChange<PersonalInfoField>[] {
  const changes: FieldChange<PersonalInfoField>[] = [];
  const wasPhone = before.phone.trim();
  const nowPhone = edited.phone.trim();
  if (wasPhone !== nowPhone) {
    changes.push({
      field: "personalPhone",
      label: "Personal phone",
      before: wasPhone,
      after: nowPhone,
    });
  }
  const wasAddress = normalizeAddress(before.address);
  const nowAddress = normalizeAddress(edited.address);
  if (JSON.stringify(wasAddress) !== JSON.stringify(nowAddress)) {
    changes.push({
      field: "personalAddress",
      label: "Personal address",
      before: formatAddress(wasAddress),
      after: formatAddress(nowAddress),
    });
  }
  return changes;
}

/** Validates only the fields the edit changed, so legacy values never block an unrelated edit. */
export function validatePersonalInfo(
  before: PersonalInfoDraft,
  edited: PersonalInfoDraft,
): PersonalInfoErrors {
  const changed = new Set(diffPersonalInfo(before, edited).map((c) => c.field));
  const errors: PersonalInfoErrors = {};
  if (changed.has("personalPhone")) {
    const phoneError = phoneValidator(edited.phone.trim());
    if (phoneError) errors.personalPhone = phoneError;
  }
  if (changed.has("personalAddress")) {
    const addressProblem = addressError(normalizeAddress(edited.address));
    if (addressProblem) errors.personalAddress = addressProblem;
  }
  return errors;
}

/** How support names the person: the owner of an individual business, otherwise the representative. */
export function personRoleOf(
  record: PersonRecord | null,
): "owner" | "representative" {
  return record === "representative" ? "representative" : "owner";
}

/** What the merchant already has, which decides where a personal value shows up as a fallback. */
export interface PersonalEffectContext {
  record: PersonRecord | null;
  /** Receipts read `company` as a whole, so any record, even one without an address, stops their fallback. */
  hasCompanyRecord: boolean;
  /** The microsite falls back field by field, so only a `company` street address stops its fallback. */
  hasCompanyAddress: boolean;
  hasSupportPhone: boolean;
  /** The `company` record's phone, which the microsite prefers over the owner's. */
  hasCompanyPhone: boolean;
  /** The owner's phone, which SMS invoices prefer over the representative's. */
  hasIndividualPhone: boolean;
}

/** The fallback facts `personalEffectsOf` needs, read from the merchant's row. */
export function personalEffectContextOf(
  business: BusinessProfile,
): PersonalEffectContext {
  return {
    record: personRecordOf(business),
    hasCompanyRecord: !!business.company,
    hasCompanyAddress: !!business.company?.addressLine1?.trim(),
    hasSupportPhone: !!business.businessSupportPhone?.trim(),
    hasCompanyPhone: !!business.company?.phone?.trim(),
    hasIndividualPhone: !!business.individual?.phone?.trim(),
  };
}

/**
 * The "This will also" lines for the Review step. The business record is not
 * touched, but receipts, the microsite and SMS invoices fall back to the
 * owner's address and phone (or the representative's phone) when the
 * business has none of its own.
 */
export function personalEffectsOf(
  changes: FieldChange<PersonalInfoField>[],
  {
    record,
    hasCompanyRecord,
    hasCompanyAddress,
    hasSupportPhone,
    hasCompanyPhone,
    hasIndividualPhone,
  }: PersonalEffectContext,
): string[] {
  const changed = (field: PersonalInfoField) =>
    changes.some((c) => c.field === field);
  const isOwner = record === "individual";
  const addressShows =
    changed("personalAddress") && isOwner && !hasCompanyAddress;
  const phoneOnSms =
    changed("personalPhone") &&
    !hasSupportPhone &&
    (isOwner || !hasIndividualPhone);
  return [
    `update the ${personRoleOf(record)}'s details in Stripe, which may ask them to re-verify`,
    "leave the business name, phone and address unchanged",
    ...(addressShows
      ? [
          hasCompanyRecord
            ? "the public microsite prints this address until a business address is added"
            : "receipts and the public microsite print this address until a business address is added",
        ]
      : []),
    ...(phoneOnSms
      ? [
          isOwner && !hasCompanyPhone
            ? "SMS invoices and the public microsite show this phone until a support phone is added"
            : "SMS invoices show this phone until a support phone is added",
        ]
      : []),
  ];
}

/**
 * Only the changed fields, merged into the person record of `row`. Pass the
 * live row: the whole nested record is rewritten.
 */
export function buildPersonalInfoPatch(
  row: BusinessProfile,
  edited: PersonalInfoDraft,
  changes: FieldChange<PersonalInfoField>[],
): PersonalInfoPatch {
  const found = personOf(row);
  if (!found) throw new Error("This merchant has no person record to edit");
  const changed = new Set(changes.map((c) => c.field));
  const edits = {
    ...(changed.has("personalPhone") ? { phone: edited.phone.trim() } : {}),
    ...(changed.has("personalAddress")
      ? addressColumnsOf(normalizeAddress(edited.address))
      : {}),
  };
  return found.record === "individual"
    ? { id: row.id, individual: { ...found.person, ...edits } }
    : { id: row.id, representative: { ...found.person, ...edits } };
}
